import React, { useContext } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { AuthProvider } from "./core/context/AuthContext";
import { ThemeProvider } from "./core/context/ThemeContext";
import { UserPreferencesProvider } from "./core/context/UserPreferencesContext";
import { MetadataProvider } from "./editor/context/MetadataContext";
import Login from "./core/auth/Login";
import Register from "./core/auth/Register";
import { GoogleAuthCallback } from "./core/auth/google";
import Dashboard from "./editor/dashboard/Dashboard";
import CreateNewWebsite from "./editor/dashboard/create/CreateNewWebsite";
import Messages from "./editor/dashboard/messages/Messages";
import ProtectedRoute from "./core/components/ProtectedRoute";
import Website from "./renderer/pages/Website";
import WebsiteVersionView from "./renderer/pages/WebsiteVersionView";
import NotFoundWebsite from "./renderer/pages/NotFoundWebsite";
import Landing from "./app/landing/Landing";
import LandingV2 from "./app/landing_v2/LandingV2";
import LandingV3 from "./app/landing_v3/LandingV3";
import LandingV4 from "./app/landing_v4/LandingV4";
import LandingV5 from "./app/landing_v5/LandingV5";
import CouponLanding from "./app/coupon/CouponLanding";
import PrivacyPolicy from "./app/legal/PrivacyPolicy";
import TermsOfUse from "./app/legal/TermsOfUse";
import AffiliateTermsOfUse from "./app/legal/AffiliateTermsOfUse";
import Feedback from "./app/feedback/Feedback";
import Blog from "./app/blog/Blog";
import BlogArticle from "./app/blog/BlogArticle";
import EditWebsitePage from "./editor/page/EditWebsitePage";
import EditRedirectPage from "./editor/page/EditRedirectPage";
import ForgotPassword from "./core/auth/ForgotPassword";
import CompleteAccount from "./core/auth/CompleteAccount";
import ResetPassword from "./core/auth/ResetPassword";
import UnsubscribeMarketing from "./core/auth/UnsubscribeMarketing";
import { ShowcasePage } from "./design-system/showcase";
import AdminLogin from "./admin/AdminLogin";
import AdminDashboardShell from "./admin/Dashboard";
import UsersAdminPage from "./admin/pages/UsersAdminPage";
import CouponsAdminPage from "./admin/pages/CouponsAdminPage";
import GlobalDiscountAdminPage from "./admin/pages/GlobalDiscountAdminPage";
import GlobalAccessGrantAdminPage from "./admin/pages/GlobalAccessGrantAdminPage";
import SubscriptionConfigAdminPage from "./admin/pages/SubscriptionConfigAdminPage";
import WebsitePreviewsAdminPage from "./admin/pages/WebsitePreviewsAdminPage";
import FeedbacksAdminPage from "./admin/pages/FeedbacksAdminPage";
import LeadsAdminPage from "./admin/pages/LeadsAdminPage";
import LandingWebsitesAdminPage from "./admin/pages/LandingWebsitesAdminPage";
import UserDetailPage from "./admin/UserDetailPage";
import AffiliatesAdminPage from "./admin/pages/AffiliatesAdminPage";
import UsersManageAdminPage from "./admin/pages/UsersManageAdminPage";
import BroadcastsAdminPage from "./admin/pages/BroadcastsAdminPage";
import AutomationsAdminPage from "./admin/pages/AutomationsAdminPage";
import MessagesAdminPage from "./admin/pages/MessagesAdminPage";
import AccountDeletionsAdminPage from "./admin/pages/AccountDeletionsAdminPage";
import BlogAdminPage from "./admin/pages/BlogAdminPage";
import AdminProtectedRoute from "./core/components/AdminProtectedRoute";
import { AdminAuthProvider } from "./admin/context/AdminAuthContext";
import { AdminAuthContext } from "./admin/context/AdminAuthContext";
import AffiliateLogin from "./affiliate/AffiliateLogin";
import AffiliateDashboardShell from "./affiliate/Dashboard";
import AffiliateProtectedRoute from "./core/components/AffiliateProtectedRoute";
import { AffiliateAuthProvider } from "./affiliate/context/AffiliateAuthContext";
import MyCouponsPage from "./affiliate/pages/MyCouponsPage";
import MyPaymentsPage from "./affiliate/pages/MyPaymentsPage";
import AnalyticsPage from "./affiliate/pages/AnalyticsPage";
import BuyCouponsPage from "./affiliate/pages/BuyCouponsPage";
import SendAccessCouponsPage from "./affiliate/pages/SendAccessCouponsPage";
import PartnerTermsOfUse from "./app/legal/PartnerTermsOfUse";
import { isPartnerKind } from "./affiliate/utils/portalKind";
import { usePortalBasePath } from "./affiliate/hooks/usePortalBasePath";
import AdminAffiliatePaymentsPage from "./admin/pages/AffiliatePaymentsAdminPage";
import { AffiliateAuthContext } from "./affiliate/context/AffiliateAuthContext";

// Create a client instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      cacheTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const AdminIndexRedirect = () => {
    const { adminAuth } = useContext(AdminAuthContext);
    if (adminAuth.isChecking) return null;
    return adminAuth.isAuthenticated ? (
      <Navigate to="/admin/dashboard/dashboard" replace />
    ) : (
      <Navigate to="/admin/login" replace />
    );
  };

  // Shared by /affiliate and /partner (interchangeable prefixes for the same portal).
  const AffiliateIndexRedirect = () => {
    const { affiliateAuth } = useContext(AffiliateAuthContext);
    const basePath = usePortalBasePath();
    if (affiliateAuth.isChecking) return null;
    if (affiliateAuth.isPortalBlocked) {
      return <Navigate to={`${basePath}/dashboard/coupons`} replace />;
    }
    return affiliateAuth.isAuthenticated ? (
      <Navigate to={`${basePath}/dashboard/coupons`} replace />
    ) : (
      <Navigate to={`${basePath}/login`} replace />
    );
  };

  // Partners have no payouts — bounce them off the payments tab to My Coupons.
  const AffiliatePaymentsRoute = () => {
    const { affiliateAuth } = useContext(AffiliateAuthContext);
    const basePath = usePortalBasePath();
    if (isPartnerKind(affiliateAuth?.user)) {
      return <Navigate to={`${basePath}/dashboard/coupons`} replace />;
    }
    return <MyPaymentsPage />;
  };

  // Legacy bookmarks: /influencer/* was renamed to /affiliate/*. Rewrite the
  // prefix and preserve the rest of the path, query, and hash.
  const LegacyAffiliatePathRedirect = () => {
    const location = useLocation();
    const target =
      location.pathname.replace(/^\/influencer/, "/affiliate") + location.search + location.hash;
    return <Navigate to={target} replace />;
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <AdminAuthProvider>
            <AffiliateAuthProvider>
            <UserPreferencesProvider>
              <MetadataProvider>
                <Router>
                <Routes>
                  {/* Design System Showcase - Internal Dev Tool */}
                  <Route path="/design-system-showcase" element={<ShowcasePage />} />
                
                {/* Admin routes */}
                <Route path="/admin" element={<AdminIndexRedirect />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin/dashboard"
                  element={
                    <AdminProtectedRoute>
                      <AdminDashboardShell />
                    </AdminProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="dashboard" replace />} />

                  {/* Dashboard overview */}
                  <Route path="dashboard" element={<UsersAdminPage />} />

                  {/* Users */}
                  <Route path="users" element={<UsersManageAdminPage />} />
                  <Route path="users/:userId" element={<UserDetailPage />} />

                  {/* Affiliates (create provisional accounts) */}
                  <Route path="affiliates" element={<AffiliatesAdminPage />} />

                  {/* Legacy paths: keep working via redirect */}
                  <Route path="influencers" element={<Navigate to="/admin/dashboard/affiliates" replace />} />
                  <Route path="coupons" element={<CouponsAdminPage />} />
                  <Route path="global-discount" element={<GlobalDiscountAdminPage />} />
                  <Route path="global-access-grant" element={<GlobalAccessGrantAdminPage />} />
                  <Route path="subscription-config" element={<SubscriptionConfigAdminPage />} />
                  <Route path="website-previews" element={<WebsitePreviewsAdminPage />} />
                  <Route path="feedbacks" element={<FeedbacksAdminPage />} />
                  <Route path="leads" element={<LeadsAdminPage />} />
                  <Route path="landing-websites" element={<LandingWebsitesAdminPage />} />
                  <Route path="broadcasts" element={<BroadcastsAdminPage />} />
                  <Route path="automations" element={<AutomationsAdminPage />} />
                  <Route path="messages" element={<MessagesAdminPage />} />
                  <Route path="account-deletions" element={<AccountDeletionsAdminPage />} />
                  <Route path="blog" element={<BlogAdminPage />} />
                  <Route path="affiliate-payments" element={<AdminAffiliatePaymentsPage />} />
                </Route>

                {/* Affiliate routes */}
                <Route path="/affiliate" element={<AffiliateIndexRedirect />} />
                <Route path="/affiliate/login" element={<AffiliateLogin />} />
                <Route
                  path="/affiliate/dashboard"
                  element={
                    <AffiliateProtectedRoute>
                      <AffiliateDashboardShell />
                    </AffiliateProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="coupons" replace />} />
                  <Route path="coupons" element={<MyCouponsPage />} />
                  <Route path="buy-coupons" element={<BuyCouponsPage />} />
                  <Route path="send-access-coupons" element={<SendAccessCouponsPage />} />
                  <Route path="payments" element={<AffiliatePaymentsRoute />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                </Route>

                {/* Partner routes — interchangeable aliases of /affiliate/* rendering the
                    exact same components; the view follows the user's commercial_kind. */}
                <Route path="/partner" element={<AffiliateIndexRedirect />} />
                <Route path="/partner/login" element={<AffiliateLogin />} />
                <Route
                  path="/partner/dashboard"
                  element={
                    <AffiliateProtectedRoute>
                      <AffiliateDashboardShell />
                    </AffiliateProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="coupons" replace />} />
                  <Route path="coupons" element={<MyCouponsPage />} />
                  <Route path="buy-coupons" element={<BuyCouponsPage />} />
                  <Route path="send-access-coupons" element={<SendAccessCouponsPage />} />
                  <Route path="payments" element={<AffiliatePaymentsRoute />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                </Route>

                {/* Legacy affiliate-portal paths (formerly "influencer") — matches
                    /influencer and every subpath, redirecting to /affiliate/... */}
                <Route path="/influencer/*" element={<LegacyAffiliatePathRedirect />} />
                
                {/* Explicit routes for edit pages */}
                <Route path="/edit/login" element={<Login />} />
                <Route path="/edit/register" element={<Register />} />
                {/* First-access onboarding (session required; handles its own auth redirect to avoid a ProtectedRoute loop) */}
                <Route path="/edit/complete-account" element={<CompleteAccount />} />
                <Route path="/edit/recovery" element={<ForgotPassword />} />
                <Route path="/edit/recovery/password" element={<ResetPassword />} />
                <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
                <Route
                  path="/edit/:username"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                {/* Dashboard route - redirect from /dashboard/username to /edit/username */}
                <Route
                  path="/dashboard/:username"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                {/* Create website route */}
                <Route
                  path="/dashboard/:username/create"
                  element={
                    <ProtectedRoute>
                      <CreateNewWebsite />
                    </ProtectedRoute>
                  }
                />
                {/* Messages route */}
                <Route
                  path="/dashboard/:username/messages"
                  element={
                    <ProtectedRoute>
                      <Messages />
                    </ProtectedRoute>
                  }
                />
                {/* Settings route - must come before general website routes */}
                <Route
                  path="/settings/:username/:section?"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                {/* Edit website routes with version slug - protected and username-guarded */}
                <Route
                  path="/edit/:username/:domain/:version_slug/:subdomain?"
                  element={
                    <ProtectedRoute>
                      <EditWebsitePage />
                    </ProtectedRoute>
                  }
                />
                {/* Redirect route for edit without version slug - still protected */}
                <Route
                  path="/edit/:username/:domain"
                  element={
                    <ProtectedRoute>
                      <EditRedirectPage />
                    </ProtectedRoute>
                  }
                />
                {/* Privacy Policy route */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                {/* Terms of Use route */}
                <Route path="/terms" element={<TermsOfUse />} />
                <Route path="/affiliate-terms" element={<AffiliateTermsOfUse />} />
                <Route path="/partner-terms" element={<PartnerTermsOfUse />} />
                {/* Unsubscribe route (public) */}
                <Route path="/unsubscribe" element={<UnsubscribeMarketing />} />
                {/* Feedback route */}
                <Route path="/feedback" element={<Feedback />} />
                {/* Blog routes */}
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogArticle />} />
                {/* Route for website not found */}
                <Route path="/website-not-found" element={<NotFoundWebsite />} />
                {/* Shareable coupon landing. The slug is a coupon code, with a fallback
                    for legacy affiliate-username links (resolved inside CouponLanding). */}
                <Route path="/discount/:slug" element={<CouponLanding />} />
                {/* Specific-version viewer (admins can open drafts; others 404 on drafts) */}
                <Route path="/view/:username/:domain/:version_slug" element={<WebsiteVersionView />} />
                {/* Website viewing routes */}
                <Route path="/:username/:domain/:subdomain" element={<Website />} />
                <Route path="/:username/:domain" element={<Website />} />
                {/* Home route */}
                <Route path="/" element={
                  window.location.hostname.startsWith('v5.') ? <LandingV5 /> :
                  window.location.hostname.startsWith('v4.') ? <LandingV4 /> :
                  window.location.hostname.startsWith('v3.') ? <LandingV3 /> :
                  window.location.hostname.startsWith('v2.') ? <LandingV2 /> : <Landing />
                } />
                <Route path="/v2" element={<LandingV2 />} />
                <Route path="/v3" element={<LandingV3 />} />
                <Route path="/v4" element={<LandingV4 />} />
                <Route path="/v5" element={<LandingV5 />} />
              </Routes>
              </Router>
            </MetadataProvider>
          </UserPreferencesProvider>
          </AffiliateAuthProvider>
          </AdminAuthProvider>
        </AuthProvider>
      </ThemeProvider>
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  );
}

export default App;
